/**
 * Unit conversion utilities - CENTRALIZED CONVERSION LOGIC
 * 
 * All unit conversions across the app use functions from this file.
 * This ensures consistency and makes maintenance easier.
 * 
 * Key principles:
 * - All conversions use exact formulas as specified
 * - Internal storage is always in metric (kg, g, cm) with FULL PRECISION
 * - Conversions apply only to display and user input
 * - Display values can be rounded (1-2 decimals) for readability
 * - Storage must keep full precision to avoid drift
 * - Macros (protein, carbs, fat, fibre) remain in grams at all times (no conversion)
 *   This matches US nutrition labels and avoids breaking parsing
 * 
 * Exact Conversion Formulas:
 * 1. Food Weight: oz = g / 28.349523125, g = oz * 28.349523125
 * 2. Body Weight: lb = kg * 2.20462262185, kg = lb / 2.20462262185
 * 3. Height: total_inches = cm / 2.54, ft = floor(total_inches / 12), in = total_inches % 12
 *            cm = (ft * 12 + in) * 2.54
 */

export type UnitSystem = 'metric' | 'imperial';

// Conversion constants
// Exact formula: oz = g / 28.3495 (as specified for macro conversions)
const GRAMS_PER_OUNCE = 28.3495;
const ML_PER_FL_OZ = 29.5735;
const POUNDS_PER_KG = 2.20462262185;
const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;

// ==================== Food Weight Conversions ====================

/**
 * Convert grams to ounces
 * Formula: oz = g / 28.349523125
 * Returns full precision (no rounding) for storage accuracy
 */
export function gramsToOunces(grams: number): number {
  return grams / GRAMS_PER_OUNCE;
}

/**
 * Convert ounces to grams
 * Formula: g = oz * 28.349523125
 * Returns full precision (no rounding) for storage accuracy
 */
export function ouncesToGrams(ounces: number): number {
  return ounces * GRAMS_PER_OUNCE;
}

// ==================== Liquid Volume Conversions ====================

/**
 * Convert milliliters to fluid ounces
 */
export function mlToFlOz(ml: number): number {
  return ml / ML_PER_FL_OZ;
}

/**
 * Convert fluid ounces to milliliters
 */
export function flOzToMl(flOz: number): number {
  return flOz * ML_PER_FL_OZ;
}

// ==================== Body Weight Conversions ====================

/**
 * Convert kilograms to pounds
 * Formula: lb = kg * 2.20462262185
 * Returns full precision (no rounding) for storage accuracy
 */
export function kgToPounds(kg: number): number {
  return kg * POUNDS_PER_KG;
}

/**
 * Convert pounds to kilograms
 * Formula: kg = lb / 2.20462262185
 * Returns full precision (no rounding) for storage accuracy
 */
export function poundsToKg(lb: number): number {
  return lb / POUNDS_PER_KG;
}

// ==================== Height Conversions ====================

/**
 * Convert centimeters to feet and inches
 * Formula: total_inches = cm / 2.54, ft = floor(total_inches / 12), in = total_inches % 12
 * Returns { feet, inches } where inches is 0-11.999...
 * Uses exact remainder calculation for full precision
 */
export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const totalInches = cm / CM_PER_INCH;
  const feet = Math.floor(totalInches / INCHES_PER_FOOT);
  const inches = totalInches % INCHES_PER_FOOT; // Exact remainder: totalInches % 12
  return { feet, inches };
}

/**
 * Convert feet and inches to centimeters
 * Formula: cm = (ft * 12 + in) * 2.54
 * Returns full precision (no rounding) for storage accuracy
 */
export function feetInchesToCm(feet: number, inches: number): number {
  return (feet * INCHES_PER_FOOT + inches) * CM_PER_INCH;
}

// ==================== Display Formatting ====================

/**
 * Format body weight for display
 * Metric: kg with 1 decimal
 * Imperial: lb with 1 decimal
 * Note: Input value (kg) is stored with full precision; only display is rounded
 */
export function formatBodyWeight(kg: number, unitSystem: UnitSystem): string {
  if (unitSystem === 'imperial') {
    const lb = kgToPounds(kg);
    return lb.toFixed(1);
  }
  return kg.toFixed(1);
}

/**
 * Format food weight for display
 * Metric: g with 0 decimals
 * Imperial: oz with 2 decimals
 * Note: Input value (grams) is stored with full precision; only display is rounded
 */
export function formatFoodWeight(grams: number, unitSystem: UnitSystem): string {
  if (unitSystem === 'imperial') {
    const oz = gramsToOunces(grams);
    return oz.toFixed(2);
  }
  return Math.round(grams).toString();
}

/**
 * Format liquid volume for display
 * Metric: ml with 0 decimals
 * Imperial: fl oz - avoid conversion artefacts (e.g., 15.01 fl oz when user entered 15)
 * BUG FIX D1 V5: Round to whole number if close to whole number to avoid conversion artefacts
 */
export function formatLiquidVolume(ml: number, unitSystem: UnitSystem): string {
  if (unitSystem === 'imperial') {
    const flOz = mlToFlOz(ml);
    // BUG FIX D1 V5: If value is very close to a whole number (within 0.05), round to whole number
    // This prevents showing 15.01 fl oz when user entered 15 fl oz
    const rounded = Math.round(flOz);
    if (Math.abs(flOz - rounded) < 0.05) {
      return rounded.toString();
    }
    return flOz.toFixed(2);
  }
  return Math.round(ml).toString();
}

/**
 * Format height for display
 * Metric: cm with 0 decimals
 * Imperial: ft' in" format
 */
export function formatHeight(cm: number, unitSystem: UnitSystem): string {
  if (unitSystem === 'imperial') {
    const { feet, inches } = cmToFeetInches(cm);
    return `${feet}' ${inches}"`;
  }
  return Math.round(cm).toString();
}

/**
 * Format macro (protein, carbs, fat, fibre) for display
 * ITEM 7: All macros displayed to 1 decimal place (layout-safe)
 */
export function formatMacro(grams: number, unitSystem: UnitSystem): string {
  if (unitSystem === 'imperial') {
    return (grams / GRAMS_PER_OUNCE).toFixed(2).replace(/\.?0+$/, '');
  }
  // ITEM 7: Show 1 decimal place for metric (not rounded to integer)
  return grams.toFixed(1);
}

// ==================== Input Parsing ====================

/**
 * Parse body weight input to kilograms
 * Metric: input is kg (stored with full precision)
 * Imperial: input is lb (converted to kg with full precision)
 * Returns full precision value for storage (no rounding)
 */
export function parseBodyWeightInput(value: string, unitSystem: UnitSystem): number | null {
  const num = parseFloat(value);
  if (isNaN(num) || num < 0) return null;
  
  if (unitSystem === 'imperial') {
    return poundsToKg(num); // Full precision conversion: lb / 2.20462262185
  }
  return num; // Full precision storage
}

/**
 * Parse food weight input to grams
 * Metric: input is g (stored with full precision)
 * Imperial: input is oz (converted to g with full precision)
 * Returns full precision value for storage (no rounding)
 */
export function parseFoodWeightInput(value: string, unitSystem: UnitSystem, isLiquid: boolean): number | null {
  const num = parseFloat(value);
  if (isNaN(num) || num < 0) return null;
  
  if (unitSystem === 'imperial') {
    if (isLiquid) {
      return flOzToMl(num);
    } else {
      return ouncesToGrams(num); // Full precision conversion: oz * 28.349523125
    }
  }
  // Metric: input is already in g or ml (full precision storage)
  return num;
}

/**
 * Parse height input to centimeters
 * Metric: input is cm (stored with full precision)
 * Imperial: inputs are feet and inches (converted to cm with full precision)
 * Formula: cm = (ft * 12 + in) * 2.54
 * Returns full precision value for storage (no rounding)
 */
export function parseHeightInput(
  value: string,
  unitSystem: UnitSystem,
  inchesValue?: string
): number | null {
  if (unitSystem === 'imperial') {
    const feet = parseFloat(value);
    const inches = inchesValue ? parseFloat(inchesValue) : 0;
    if (isNaN(feet) || isNaN(inches) || feet < 0 || inches < 0 || inches >= 12) return null;
    return feetInchesToCm(feet, inches); // Full precision conversion: (ft * 12 + in) * 2.54
  }
  // Metric: input is cm (full precision storage)
  const cm = parseFloat(value);
  if (isNaN(cm) || cm < 0) return null;
  return cm;
}

/**
 * Parse macro input to grams
 * ALWAYS expects grams (g) regardless of unit system
 * This matches US nutrition labels and avoids breaking parsing
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function parseMacroInput(value: string, _unitSystem: UnitSystem): number | null {
  const num = parseFloat(value);
  if (isNaN(num) || num < 0) return null;
  
  // Macros always input in grams, regardless of unit system
  return num;
}

// ==================== Unit Labels ====================

/**
 * Get body weight unit label
 */
export function getBodyWeightUnit(unitSystem: UnitSystem): string {
  return unitSystem === 'imperial' ? 'lb' : 'kg';
}

/**
 * Get food weight unit label
 */
export function getFoodWeightUnit(unitSystem: UnitSystem, isLiquid: boolean): string {
  if (unitSystem === 'imperial') {
    return isLiquid ? 'fl oz' : 'oz';
  }
  return isLiquid ? 'ml' : 'g';
}

/**
 * Get height unit label
 */
export function getHeightUnit(unitSystem: UnitSystem): string {
  return unitSystem === 'imperial' ? 'ft/in' : 'cm';
}

/**
 * Get macro unit label
 * ALWAYS returns 'g' regardless of unit system
 * This matches US nutrition labels and avoids breaking parsing
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getMacroUnit(_unitSystem: UnitSystem): string {
  // Macros always displayed in grams, regardless of unit system
  return 'g';
}

